import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ICommerceConfig } from '../../../models/Commerce';
import { AuthService } from '../../../abstraction/auth.service';
import errors from '../../../utils/errorCodes';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';

const acceptedImageExtension: string[] = ['jpg', 'jpeg', 'png'];
const maxImageSizeAccepted = 2000000;

@Component({
  selector: 'app-customization',
  templateUrl: './customization.component.html',
  styleUrls: ['./customization.component.scss'],
})
export class CustomizationComponent implements OnInit {
  formGroup: FormGroup;
  imagePreview: string;
  loading = true;

  constructor(private authService: AuthService, private dialog: MatDialog) {
    this.formGroup = new FormGroup({
      accentColor: new FormControl('', [this.colorValidator.bind(this)]),
      logoImg: new FormControl('', [this.imageValidator.bind(this)]),
      displayName: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.authService.commerceConfig.subscribe((commerceConfig: ICommerceConfig) => {
      if (commerceConfig && Object.keys(commerceConfig).length > 0) {
        this.formGroup.controls.displayName.setValue(commerceConfig.displayName);
        this.formGroup.controls.accentColor.setValue(commerceConfig.accentColor);

        //set url of image selected
        this.imagePreview = commerceConfig.logoUrl;
      }
      this.loading = false;
    });
  }

  colorPickerChange(event: Event) {
    this.formGroup.controls.accentColor.setValue((event.target as HTMLInputElement).value);
    this.formGroup.controls.accentColor.markAsDirty();
  }

  colorValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (
      control.value &&
      /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.test(control.value as string)
    ) {
      return this.getContrast(control.value as string) < 2 ? { invalidColor: true } : null;
    }
    return null;
  }

  getLuminance(r, g, b) {
    const a = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : null;
  }

  getContrast(hexColor1: string) {
    const rgb1 = this.hexToRgb(hexColor1);
    const rgb2 = this.hexToRgb('#ffffff');
    const lum1 = this.getLuminance(rgb1[0], rgb1[1], rgb1[2]);
    const lum2 = this.getLuminance(rgb2[0], rgb2[1], rgb2[2]);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  }

  setCommerceConfig() {
    if (this.formGroup.invalid) return;

    this.loading = true;
    const newCommerceConfig: ICommerceConfig = {};
    Object.keys(this.formGroup.controls).forEach((index) => {
      if (this.formGroup.controls[index].dirty) {
        newCommerceConfig[index] = this.formGroup.controls[index].value;
      }
    });

    this.authService.setCommerConfig(newCommerceConfig).subscribe(
      (res) => {
        if (res) {
          this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
            panelClass: 'modal-container',
            data: {
              title: 'Cambios guardados',
            },
          });
          this.loading = false;
          this.formGroup.markAsPristine();
        }
      },
      (error: HttpErrorResponse) => {
        this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
          panelClass: 'modal-container',
          data: {
            title: 'Error al guardar cambios',
            errorCode: error.error.errorCode,
            isError: true,
          },
        });
        this.loading = false;
      },
    );
  }

  readImage(event: Event) {
    const files: FileList = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const extension = files[0].type.split('/').pop().toLowerCase();
      if (
        files[0].size > maxImageSizeAccepted ||
        acceptedImageExtension.indexOf(extension) === -1
      ) {
        this.handleUploadError();
        return;
      }

      const reader = new FileReader();
      reader.onload = (onloadEvent: ProgressEvent<FileReader>) => {
        this.imagePreview = onloadEvent.target.result as string;
        this.formGroup.controls.logoImg.setValue(this.imagePreview);
        this.formGroup.controls.logoImg.markAsDirty();
      };
      reader.onerror = (err) => {
        this.handleUploadError();
      };

      reader.readAsDataURL(files[0]);
    }
  }

  handleUploadError(): void {
    this.formGroup.controls.logoImg.setValue(undefined);
    this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
      panelClass: 'modal-container',
      data: {
        title: 'Error en subir archivo',
        content: 'El tamaño máximo permitido es de 2 MB. Los formatos válidos son jpg, jpeg y png.',
        isError: true,
      },
    });
  }

  imageValidator(control: AbstractControl): { [key: string]: boolean } | null {
    return !control.value && !this.imagePreview ? { required: true } : null;
  }

  getErrorMessage(input: string) {
    const keys = Object.keys(this.formGroup.controls[input].errors);
    if (keys.length === 0) {
      return '';
    }
    return errors[keys[0]];
  }
}
